import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject, Optional, EventEmitter, Component, Input, HostBinding, Output, NgModule, forwardRef, Directive, HostListener } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { of, BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

/** @deprecated Use `LOADER_OPTIONS` instead. See `RecaptchaLoaderOptions.onBeforeLoad` */
const RECAPTCHA_LANGUAGE = /*#__PURE__*/new InjectionToken("recaptcha-language");
/** @deprecated Use `LOADER_OPTIONS` instead. See `RecaptchaLoaderOptions.onBeforeLoad` */
const RECAPTCHA_BASE_URL = /*#__PURE__*/new InjectionToken("recaptcha-base-url");
/** @deprecated Use `LOADER_OPTIONS` instead. See `RecaptchaLoaderOptions.onBeforeLoad` */
const RECAPTCHA_NONCE = /*#__PURE__*/new InjectionToken("recaptcha-nonce-tag");
const RECAPTCHA_SETTINGS = /*#__PURE__*/new InjectionToken("recaptcha-settings");
const RECAPTCHA_V3_SITE_KEY = /*#__PURE__*/new InjectionToken("recaptcha-v3-site-key");
/**
 * See the documentation for `RecaptchaLoaderOptions`.
 */
const RECAPTCHA_LOADER_OPTIONS = /*#__PURE__*/new InjectionToken("recaptcha-loader-options");
function loadScript(renderMode, onBeforeLoad, onLoaded, {
  url,
  lang,
  nonce
} = {}) {
  window.ng2recaptchaloaded = () => {
    onLoaded(grecaptcha);
  };
  const script = document.createElement("script");
  script.innerHTML = "";
  const {
    url: baseUrl,
    nonce: onBeforeLoadNonce
  } = onBeforeLoad(new URL(url || "https://www.google.com/recaptcha/api.js"));
  baseUrl.searchParams.set("render", renderMode === "explicit" ? renderMode : renderMode.key);
  baseUrl.searchParams.set("onload", "ng2recaptchaloaded");
  baseUrl.searchParams.set("trustedtypes", "true");
  if (lang) {
    baseUrl.searchParams.set("hl", lang);
  }
  script.src = baseUrl.href;
  const nonceValue = onBeforeLoadNonce || nonce;
  if (nonceValue) {
    script.setAttribute("nonce", nonceValue);
  }
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}
function newLoadScript({
  v3SiteKey,
  onBeforeLoad,
  onLoaded
}) {
  const renderMode = v3SiteKey ? {
    key: v3SiteKey
  } : "explicit";
  loader.loadScript(renderMode, onBeforeLoad, onLoaded);
}
const loader = {
  loadScript,
  newLoadScript
};
function toNonNullObservable(subject) {
  return subject.asObservable().pipe(filter(value => value !== null));
}
let RecaptchaLoaderService = /*#__PURE__*/(() => {
  class RecaptchaLoaderService {
    /**
     * @internal
     * @nocollapse
     */
    static {
      this.ready = null;
    }
    constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    platformId,
    // eslint-disable-next-line deprecation/deprecation
    language,
    // eslint-disable-next-line deprecation/deprecation
    baseUrl,
    // eslint-disable-next-line deprecation/deprecation
    nonce, v3SiteKey, options) {
      this.platformId = platformId;
      this.language = language;
      this.baseUrl = baseUrl;
      this.nonce = nonce;
      this.v3SiteKey = v3SiteKey;
      this.options = options;
      const subject = this.init();
      this.ready = subject ? toNonNullObservable(subject) : of();
    }
    /** @internal */
    init() {
      if (RecaptchaLoaderService.ready) {
        return RecaptchaLoaderService.ready;
      }
      if (!isPlatformBrowser(this.platformId)) {
        return undefined;
      }
      const subject = new BehaviorSubject(null);
      RecaptchaLoaderService.ready = subject;
      loader.newLoadScript({
        v3SiteKey: this.v3SiteKey,
        onBeforeLoad: url => {
          if (this.options?.onBeforeLoad) {
            return this.options.onBeforeLoad(url);
          }
          const newUrl = new URL(this.baseUrl ?? url);
          if (this.language) {
            newUrl.searchParams.set("hl", this.language);
          }
          return {
            url: newUrl,
            nonce: this.nonce
          };
        },
        onLoaded: recaptcha => {
          let value = recaptcha;
          if (this.options?.onLoaded) {
            value = this.options.onLoaded(recaptcha);
          }
          subject.next(value);
        }
      });
      return subject;
    }
    static {
      this.ɵfac = function RecaptchaLoaderService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RecaptchaLoaderService)(i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(RECAPTCHA_LANGUAGE, 8), i0.ɵɵinject(RECAPTCHA_BASE_URL, 8), i0.ɵɵinject(RECAPTCHA_NONCE, 8), i0.ɵɵinject(RECAPTCHA_V3_SITE_KEY, 8), i0.ɵɵinject(RECAPTCHA_LOADER_OPTIONS, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RecaptchaLoaderService,
        factory: RecaptchaLoaderService.ɵfac
      });
    }
  }
  return RecaptchaLoaderService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let nextId = 0;
let RecaptchaComponent = /*#__PURE__*/(() => {
  class RecaptchaComponent {
    constructor(elementRef, loader, zone, settings) {
      this.elementRef = elementRef;
      this.loader = loader;
      this.zone = zone;
      this.id = `ngrecaptcha-${nextId++}`;
      this.errorMode = "default";
      this.resolved = new EventEmitter();
      /**
       * @deprecated `(error) output will be removed in the next major version. Use (errored) instead
       */
      // eslint-disable-next-line @angular-eslint/no-output-native
      this.error = new EventEmitter();
      this.errored = new EventEmitter();
      if (settings) {
        this.siteKey = settings.siteKey;
        this.theme = settings.theme;
        this.type = settings.type;
        this.size = settings.size;
        this.badge = settings.badge;
      }
    }
    ngAfterViewInit() {
      this.subscription = this.loader.ready.subscribe(grecaptcha => {
        if (grecaptcha != null && grecaptcha.render instanceof Function) {
          this.grecaptcha = grecaptcha;
          this.renderRecaptcha();
        }
      });
    }
    ngOnDestroy() {
      // reset the captcha to ensure it does not leave anything behind
      // after the component is no longer needed
      this.grecaptchaReset();
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
    }
    /**
     * Executes the invisible recaptcha.
     * Does nothing if component's size is not set to "invisible".
     */
    execute() {
      if (this.size !== "invisible") {
        return;
      }
      if (this.widget != null) {
        void this.grecaptcha.execute(this.widget);
      } else {
        // delay execution of recaptcha until it actually renders
        this.executeRequested = true;
      }
    }
    reset() {
      if (this.widget != null) {
        if (this.grecaptcha.getResponse(this.widget)) {
          // Only emit an event in case if something would actually change.
          // That way we do not trigger "touching" of the control if someone does a "reset"
          // on a non-resolved captcha.
          this.resolved.emit(null);
        }
        this.grecaptchaReset();
      }
    }
    /**
     * ⚠️ Warning! Use this property at your own risk!
     *
     * While this member is `public`, it is not a part of the component's public API.
     * The semantic versioning guarantees _will not be honored_! Thus, you might find that this property behavior changes in incompatible ways in minor or even patch releases.
     * You are **strongly advised** against using this property.
     * Instead, use more idiomatic ways to get reCAPTCHA value, such as `resolved` EventEmitter, or form-bound methods (ngModel, formControl, and the likes).å
     */
    get __unsafe_widgetValue() {
      return this.widget != null ? this.grecaptcha.getResponse(this.widget) : null;
    }
    /** @internal */
    expired() {
      this.resolved.emit(null);
    }
    /** @internal */
    onError(args) {
      // eslint-disable-next-line deprecation/deprecation
      this.error.emit(args);
      this.errored.emit(args);
    }
    /** @internal */
    captchaResponseCallback(response) {
      this.resolved.emit(response);
    }
    /** @internal */
    grecaptchaReset() {
      if (this.widget != null) {
        this.zone.runOutsideAngular(() => this.grecaptcha.reset(this.widget));
      }
    }
    /** @internal */
    renderRecaptcha() {
      // This `any` can be removed after @types/grecaptcha get updated
      const renderOptions = {
        badge: this.badge,
        callback: response => {
          this.zone.run(() => this.captchaResponseCallback(response));
        },
        "expired-callback": () => {
          this.zone.run(() => this.expired());
        },
        sitekey: this.siteKey,
        size: this.size,
        tabindex: this.tabIndex,
        theme: this.theme,
        type: this.type
      };
      if (this.errorMode === "handled") {
        renderOptions["error-callback"] = (...args) => {
          this.zone.run(() => this.onError(args));
        };
      }
      this.widget = this.grecaptcha.render(this.elementRef.nativeElement, renderOptions);
      if (this.executeRequested === true) {
        this.executeRequested = false;
        this.execute();
      }
    }
    static {
      this.ɵfac = function RecaptchaComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RecaptchaComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(RecaptchaLoaderService), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(RECAPTCHA_SETTINGS, 8));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: RecaptchaComponent,
        selectors: [["re-captcha"]],
        hostVars: 1,
        hostBindings: function RecaptchaComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("id", ctx.id);
          }
        },
        inputs: {
          id: "id",
          siteKey: "siteKey",
          theme: "theme",
          type: "type",
          size: "size",
          tabIndex: "tabIndex",
          badge: "badge",
          errorMode: "errorMode"
        },
        outputs: {
          resolved: "resolved",
          error: "error",
          errored: "errored"
        },
        exportAs: ["reCaptcha"],
        standalone: false,
        decls: 0,
        vars: 0,
        template: function RecaptchaComponent_Template(rf, ctx) {},
        encapsulation: 2
      });
    }
  }
  return RecaptchaComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RecaptchaCommonModule = /*#__PURE__*/(() => {
  class RecaptchaCommonModule {
    static {
      this.ɵfac = function RecaptchaCommonModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RecaptchaCommonModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: RecaptchaCommonModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return RecaptchaCommonModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RecaptchaModule = /*#__PURE__*/(() => {
  class RecaptchaModule {
    static {
      this.ɵfac = function RecaptchaModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RecaptchaModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: RecaptchaModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [RecaptchaLoaderService],
        imports: [RecaptchaCommonModule]
      });
    }
  }
  return RecaptchaModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The main service for working with reCAPTCHA v3 APIs.
 *
 * Use the `execute` method for executing a single action, and
 * `onExecute` observable for listening to all actions at once.
 */
let ReCaptchaV3Service = /*#__PURE__*/(() => {
  class ReCaptchaV3Service {
    constructor(zone, recaptchaLoader, siteKey) {
      this.recaptchaLoader = recaptchaLoader;
      this.zone = zone;
      this.siteKey = siteKey;
      this.init();
    }
    get onExecute() {
      if (!this.onExecuteSubject) {
        this.onExecuteSubject = new Subject();
        this.onExecuteObservable = this.onExecuteSubject.asObservable();
      }
      return this.onExecuteObservable;
    }
    get onExecuteError() {
      if (!this.onExecuteErrorSubject) {
        this.onExecuteErrorSubject = new Subject();
        this.onExecuteErrorObservable = this.onExecuteErrorSubject.asObservable();
      }
      return this.onExecuteErrorObservable;
    }
    /**
     * Executes the provided `action` with reCAPTCHA v3 API.
     * Use the emitted token value for verification purposes on the backend.
     *
     * For more information about reCAPTCHA v3 actions and tokens refer to the official documentation at
     * https://developers.google.com/recaptcha/docs/v3.
     *
     * @param {string} action the action to execute
     * @returns {Observable<string>} an `Observable` that will emit the reCAPTCHA v3 string `token` value whenever ready.
     * The returned `Observable` completes immediately after emitting a value.
     */
    execute(action) {
      const subject = new Subject();
      if (!this.grecaptcha) {
        if (!this.actionBacklog) {
          this.actionBacklog = [];
        }
        this.actionBacklog.push([action, subject]);
      } else {
        this.executeActionWithSubject(action, subject);
      }
      return subject.asObservable();
    }
    /** @internal */
    executeActionWithSubject(action, subject) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const onError = error => {
        this.zone.run(() => {
          subject.error(error);
          if (this.onExecuteErrorSubject) {
            // We don't know any better at this point, unfortunately, so have to resort to `any`
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.onExecuteErrorSubject.next({
              action,
              error
            });
          }
        });
      };
      this.zone.runOutsideAngular(() => {
        try {
          this.grecaptcha.execute(this.siteKey, {
            action
          }).then(token => {
            this.zone.run(() => {
              subject.next(token);
              subject.complete();
              if (this.onExecuteSubject) {
                this.onExecuteSubject.next({
                  action,
                  token
                });
              }
            });
          }, onError);
        } catch (e) {
          onError(e);
        }
      });
    }
    /** @internal */
    init() {
      this.recaptchaLoader.ready.subscribe(value => {
        this.grecaptcha = value;
        if (this.actionBacklog && this.actionBacklog.length > 0) {
          this.actionBacklog.forEach(([action, subject]) => this.executeActionWithSubject(action, subject));
          this.actionBacklog = undefined;
        }
      });
    }
    static {
      this.ɵfac = function ReCaptchaV3Service_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ReCaptchaV3Service)(i0.ɵɵinject(i0.NgZone), i0.ɵɵinject(RecaptchaLoaderService), i0.ɵɵinject(RECAPTCHA_V3_SITE_KEY));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ReCaptchaV3Service,
        factory: ReCaptchaV3Service.ɵfac
      });
    }
  }
  return ReCaptchaV3Service;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RecaptchaV3Module = /*#__PURE__*/(() => {
  class RecaptchaV3Module {
    static {
      this.ɵfac = function RecaptchaV3Module_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RecaptchaV3Module)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: RecaptchaV3Module
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [ReCaptchaV3Service, RecaptchaLoaderService]
      });
    }
  }
  return RecaptchaV3Module;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RecaptchaValueAccessorDirective = /*#__PURE__*/(() => {
  class RecaptchaValueAccessorDirective {
    constructor(host) {
      this.host = host;
      this.requiresControllerReset = false;
    }
    writeValue(value) {
      if (!value) {
        this.host.reset();
      } else {
        // In this case, it is most likely that a form controller has requested to write a specific value into the component.
        // This isn't really a supported case - reCAPTCHA values are single-use, and, in a sense, readonly.
        // What this means is that the form controller has recaptcha control state of X, while reCAPTCHA itself can't "restore"
        // to that state. In order to make form controller aware of this discrepancy, and to fix the said misalignment,
        // we'll be telling the controller to "reset" the value back to null.
        if (this.host.__unsafe_widgetValue !== value && Boolean(this.host.__unsafe_widgetValue) === false) {
          this.requiresControllerReset = true;
        }
      }
    }
    registerOnChange(fn) {
      this.onChange = fn;
      if (this.requiresControllerReset) {
        this.requiresControllerReset = false;
        this.onChange(null);
      }
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    onResolve($event) {
      if (this.onChange) {
        this.onChange($event);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    }
    static {
      this.ɵfac = function RecaptchaValueAccessorDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RecaptchaValueAccessorDirective)(i0.ɵɵdirectiveInject(RecaptchaComponent));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: RecaptchaValueAccessorDirective,
        selectors: [["re-captcha", "formControlName", ""], ["re-captcha", "formControl", ""], ["re-captcha", "ngModel", ""]],
        hostBindings: function RecaptchaValueAccessorDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("resolved", function RecaptchaValueAccessorDirective_resolved_HostBindingHandler($event) {
              return ctx.onResolve($event);
            });
          }
        },
        standalone: false,
        features: [i0.ɵɵProvidersFeature([{
          multi: true,
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => RecaptchaValueAccessorDirective)
        }])]
      });
    }
  }
  return RecaptchaValueAccessorDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RecaptchaFormsModule = /*#__PURE__*/(() => {
  class RecaptchaFormsModule {
    static {
      this.ɵfac = function RecaptchaFormsModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RecaptchaFormsModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: RecaptchaFormsModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [FormsModule, RecaptchaCommonModule]
      });
    }
  }
  return RecaptchaFormsModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { RECAPTCHA_BASE_URL, RECAPTCHA_LANGUAGE, RECAPTCHA_LOADER_OPTIONS, RECAPTCHA_NONCE, RECAPTCHA_SETTINGS, RECAPTCHA_V3_SITE_KEY, ReCaptchaV3Service, RecaptchaComponent, RecaptchaFormsModule, RecaptchaLoaderService, RecaptchaModule, RecaptchaV3Module, RecaptchaValueAccessorDirective };
